import { type SuretyAccountId } from "@inrev/common";
import {
	useAdminFetchAccountBondingLines,
	useAdminFetchProjectedAccountExposure,
	useAdminGetCarrierPreviews,
} from "@portal/api/account/admin";
import { useMemo } from "react";
import { normalizeToError } from "../../../utils/error";
import { CenteredSpinner } from "../../ui/CenteredSpinner";
import { ReportedRetryableLoadError } from "../load-errors";
import { buildExposureBreakdownViews, exposureLimitSummary } from "./buildExposureBreakdownViews";
import { ExposureWithLimit } from "./ExposureWithLimit";

export type AdminExposureSectionProps = {
	accountId: SuretyAccountId;
	testId?: string;
};

export const AdminExposureSection = ({
	accountId,
	testId = "admin-exposure",
}: AdminExposureSectionProps) => {
	const { bondingLines, bondingLinesError, bondingLinesIsLoading, refetchBondingLines } =
		useAdminFetchAccountBondingLines(accountId);
	const { exposure, exposureError, exposureIsLoading, refetchExposure } =
		useAdminFetchProjectedAccountExposure(accountId);
	const { allCarriers } = useAdminGetCarrierPreviews();

	const { views, limitSummary } = useMemo(() => {
		if (exposure === undefined) return { views: [], limitSummary: undefined };
		const options = {
			limitVariant: "admin" as const,
			aggregateLimit: bondingLines?.bondingLine?.aggregateLimit,
			carriers: allCarriers,
		};
		return {
			views: buildExposureBreakdownViews(exposure, options),
			limitSummary: exposureLimitSummary(exposure, options),
		};
	}, [allCarriers, bondingLines?.bondingLine?.aggregateLimit, exposure]);

	if (exposureError) {
		return (
			<ReportedRetryableLoadError
				error={normalizeToError(exposureError)}
				resource="exposure"
				onRetry={refetchExposure}
				testId={`${testId}-error`}
			/>
		);
	}

	if (bondingLinesError) {
		return (
			<ReportedRetryableLoadError
				error={normalizeToError(bondingLinesError)}
				resource="bonding lines"
				onRetry={refetchBondingLines}
				testId={`${testId}-error`}
			/>
		);
	}

	if (exposureIsLoading || bondingLinesIsLoading) {
		return (
			<div className="min-h-40">
				<CenteredSpinner testId={`${testId}-spinner`} />
			</div>
		);
	}

	if (exposure === undefined) {
		return null;
	}

	return <ExposureWithLimit views={views} limitSummary={limitSummary} testId={testId} />;
};
